import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import BrandLogo from "../../components/common/BrandLogo";
import PageTagline from "../../components/common/PageTagline";
import Spin from "../../components/common/Spin";
import { T } from "../../styles/GlobalStyles";
import { useAuth } from "../../contexts/AuthContext";
import { isSupabaseConfigured } from "../../services/supabase";
import { getErrorMessage } from "../../utils/microcopy";
import { pickDailyPhrase } from "../../utils/pickDailyPhrase";
import { track, EVENTS } from "../../services/analytics";

const TAGLINES_SIGNUP = [
  "Moins d'admin. Plus de terrain.",
  "Ton business méritait mieux qu'un tableur.",
  "Un coach, une app, zéro bordel.",
  "T'as pas besoin d'une équipe. T'as AthleOS.",
  "Le coaching pro, ça ressemble à ça.",
];

const TAGLINES_LOGIN = [
  "Content de te revoir, Coach.",
  "Tes clients t'attendent.",
  "On reprend où on s'est arrêtés.",
  "Prêt pour une bonne journée de coaching ?",
];

function isReturningUser() {
  if (typeof localStorage === "undefined") return false;
  try {
    return Object.keys(localStorage).some((key) => key.startsWith("sb-") && key.endsWith("-auth-token"));
  } catch {
    return false;
  }
}

export default function LoginView() {
  const navigate = useNavigate();
  const { user, signInWithGoogle, signInWithEmail, signUpWithEmail } = useAuth();
  const [mode, setMode] = useState(() => (isReturningUser() ? "signin" : "signup"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(false);

  const taglinePhrase = useMemo(() => {
    const pool = mode === "signup" ? TAGLINES_SIGNUP : TAGLINES_LOGIN;
    const pageId = mode === "signup" ? "login-signup" : "login-return";
    return pickDailyPhrase(pool, pageId);
  }, [mode]);

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  if (!isSupabaseConfigured()) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: T.bg,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          gap: 16,
        }}
      >
        <p style={{ color: T.muted, textAlign: "center", maxWidth: 360, lineHeight: 1.5 }}>
          Supabase n’est pas configuré. Sans <code style={{ color: T.accent }}>.env.local</code>, je tourne en mode
          local — retour à l’app ci-dessous.
        </p>
        <Link
          to="/"
          style={{ color: T.accent, fontWeight: 700, textDecoration: "none" }}
        >
          ← Retour à l’app
        </Link>
      </div>
    );
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      if (mode === "signin") {
        await signInWithEmail(email, password);
        track(EVENTS.AUTH_LOGIN_EMAIL, { method: "email" });
      } else {
        await signUpWithEmail(email, password, { firstName, lastName });
        track(EVENTS.AUTH_SIGNUP, { method: "email" });
      }
    } catch (err) {
      setError(err?.message || getErrorMessage("validation"));
    } finally {
      setPending(false);
    }
  };

  const onGoogle = async () => {
    setError(null);
    setPending(true);
    try {
      await signInWithGoogle();
      track(EVENTS.AUTH_LOGIN_GOOGLE, { method: "google" });
    } catch (err) {
      setError(err?.message || getErrorMessage("network"));
      setPending(false);
    }
  };

  const onDemo = async () => {
    const demEmail = import.meta.env.VITE_DEMO_AUTH_EMAIL;
    const demPassword = import.meta.env.VITE_DEMO_AUTH_PASSWORD;
    if (!demEmail || !demPassword) return;
    setError(null);
    setPending(true);
    try {
      await signInWithEmail(demEmail, demPassword);
      track(EVENTS.AUTH_DEMO_LOGIN, {});
    } catch (err) {
      setError(err?.message || getErrorMessage("validation"));
    } finally {
      setPending(false);
    }
  };

  const showDemo = !!(import.meta.env.VITE_DEMO_AUTH_EMAIL && import.meta.env.VITE_DEMO_AUTH_PASSWORD);

  const inputStyle = {
    padding: "12px 14px",
    borderRadius: 8,
    border: `1px solid ${T.border}`,
    background: T.surface,
    color: T.text,
    fontSize: 14,
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: T.bg,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div style={{ width: "100%", maxWidth: 400, display: "flex", flexDirection: "column", gap: 20 }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ marginBottom: 12, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
            <BrandLogo brandName="AthleOS" size={56} />
            <div
              className="condensed"
              style={{
                fontSize: 22,
                fontWeight: 800,
                letterSpacing: "0.14em",
                color: T.accent,
                lineHeight: 1.1,
              }}
            >
              ATHLEOS
            </div>
            <div
              style={{
                fontSize: 9,
                letterSpacing: "0.22em",
                color: T.muted,
                fontWeight: 600,
                textTransform: "uppercase",
              }}
            >
              COACH PLATFORM
            </div>
          </div>
          <PageTagline phrases={[taglinePhrase]} pageId="login" hideDot />
        </div>

        <button
          type="button"
          onClick={() => void onGoogle()}
          disabled={pending}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            padding: "14px 18px",
            borderRadius: 12,
            border: `1px solid ${T.border}`,
            background: T.surface,
            color: T.text,
            fontWeight: 700,
            fontSize: 15,
            cursor: pending ? "wait" : "pointer",
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
            <path
              fill="currentColor"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="currentColor"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="currentColor"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="currentColor"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Continuer avec Google
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 12, color: T.muted, fontSize: 12 }}>
          <div style={{ flex: 1, height: 1, background: T.border }} />
          ou
          <div style={{ flex: 1, height: 1, background: T.border }} />
        </div>

        <form onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <input
            type="email"
            autoComplete="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={inputStyle}
          />
          <input
            type="password"
            autoComplete={mode === "signin" ? "current-password" : "new-password"}
            placeholder="Mot de passe"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            style={inputStyle}
          />
          {mode === "signup" && (
            <>
              <input
                type="text"
                autoComplete="given-name"
                placeholder="Prénom"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                style={inputStyle}
              />
              <input
                type="text"
                autoComplete="family-name"
                placeholder="Nom"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                style={inputStyle}
              />
            </>
          )}
          {error && (
            <p style={{ color: "#f87171", fontSize: 13, margin: 0 }} role="alert">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={pending}
            style={{
              padding: "14px 18px",
              borderRadius: 999,
              border: "none",
              background: T.accent,
              color: T.bg,
              fontWeight: 700,
              fontSize: 15,
              cursor: pending ? "wait" : "pointer",
            }}
          >
            {pending ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <Spin s={18} /> Patience…
              </span>
            ) : mode === "signin" ? (
              "Me connecter"
            ) : (
              "Créer mon compte"
            )}
          </button>
        </form>

        <button
          type="button"
          onClick={() => {
            setMode(mode === "signin" ? "signup" : "signin");
            setError(null);
          }}
          style={{ background: "none", border: "none", color: T.accent, fontSize: 13, cursor: "pointer" }}
        >
          {mode === "signin" ? "Pas encore de compte ? Créer le mien →" : "Déjà un compte ? Se connecter →"}
        </button>

        {showDemo && (
          <button
            type="button"
            onClick={() => void onDemo()}
            disabled={pending}
            style={{
              background: "none",
              border: "none",
              color: T.muted,
              fontSize: 12,
              textDecoration: "underline",
              cursor: pending ? "wait" : "pointer",
            }}
          >
            Voir la démo
          </button>
        )}
      </div>
    </div>
  );
}
